
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { v1 as uuid } from 'uuid';
import React, {
  ReactElement,
  ReactNode,
  SyntheticEvent,
  forwardRef,
  useState
} from 'react';

import Svg from 'src/components/core/svg';
import checkedIcon from 'src/assets/svg/forms/radio-button-checked.svg';
import styled, { css } from 'styled-components';
import uncheckedIcon from 'src/assets/svg/forms/radio-button-unchecked.svg';

/**
 * `RadioButtonProps` type.
 */

export type RadioButtonProps = {
  'aria-describedby'?: string,
  'aria-label'?: string,
  checked?: boolean,
  className?: string,
  disabled?: boolean,
  label?: ReactNode,
  name: string,
  onChange?: (event: SyntheticEvent) => void,
  reverse?: boolean,
  value?: any
};

/**
 * Radio button size.
 */

const radioButtonSize = units(2.5);

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<Pick<RadioButtonProps, 'disabled'>>`
  padding: 10px;
  position: relative;

  ${ifProp('disabled', css`
    cursor: default;
    pointer-events: none;
  `)}
`;

/**
 * `StyledSvg` styled component.
 */

const StyledSvg = styled(Svg).attrs({ size: radioButtonSize })`
  border-radius: 2px;
  grid-area: checkbox;
  transition: ${theme('animations.defaultTransition')};
  transition-property: color, opacity;
`;

/**
 * `CheckedIcon` styled component.
 */

const CheckedIcon = styled(StyledSvg).attrs({ icon: checkedIcon })`
  color: ${color('grey400')};
  opacity: 0;
`;

/**
 * `UncheckedIcon` styled component.
 */

const UncheckedIcon = styled(StyledSvg).attrs({ icon: uncheckedIcon })`
  color: ${color('grey300')};
  opacity: 1;
`;

/**
 * `Label` styled component.
 */

const Label = styled.label<Pick<RadioButtonProps, 'reverse'> & { hasLabel: boolean }>`
  ${theme('typography.styles.p')}

  align-items: center;
  cursor: pointer;
  display: grid;
  grid-column-gap: ${ifProp('hasLabel', 0, 10)}px;
  grid-template-areas: 'checkbox label';
  grid-template-columns: ${radioButtonSize} 1fr;

  ${ifProp('reverse', css`
    grid-template-areas: 'label checkbox';
    grid-template-columns: 1fr ${radioButtonSize};
  `)}
`;

/**
 * `LabelText` styled component.
 */

const LabelText = styled.span`
  color: ${color('grey400')};
  grid-area: label;
  transition: color ${theme('animations.defaultTransition')};
`;

/**
 * `Input` styled component.
 */

const Input = styled.input`
  cursor: pointer;
  grid-area: checkbox;
  height: ${radioButtonSize};
  opacity: 0;
  width: ${radioButtonSize};
  z-index: 1;

  &:checked {
    & ~ ${UncheckedIcon} {
      opacity: 0;
    }

    & ~ ${CheckedIcon} {
      opacity: 1;
    }
  }

  &:focus,
  &:hover {
    & ~ ${LabelText} {
      color: ${color('grey900')};
    }

    & ~ ${UncheckedIcon} {
      color: ${color('grey900')};
    }

    &:checked ~ ${CheckedIcon} {
      color: ${color('grey900')};
    }
  }

  ${ifProp('disabled', css`
    & ~ ${LabelText},
    & ~ ${UncheckedIcon},
    &:checked ~ ${CheckedIcon} {
      color: ${color('grey600')};
    }
  `)}
`;

/**
 * `RadioButton` component.
 */

const RadioButton = (props: RadioButtonProps, ref: any): ReactElement => {
  const { className, disabled, label, reverse, ...rest } = props;
  const [id] = useState(uuid);

  return (
    <Wrapper
      className={className}
      disabled={disabled}
    >
      <Label
        hasLabel={!label}
        reverse={reverse}
      >
        <Input
          disabled={disabled}
          id={id}
          ref={ref}
          type={'radio'}
          {...rest}
        />

        {label && (
          <LabelText>
            {label}
          </LabelText>
        )}

        <UncheckedIcon />

        <CheckedIcon />
      </Label>
    </Wrapper>
  );
};

/**
 * Export `RadioButton` component.
 */

export default forwardRef<RadioButtonProps, any>(RadioButton);
